import { useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { cancelRequestApi } from '../api/cancelRequestApi';
import { useHomeStore } from '../model/homeStore';
import { idempotencyKey } from '../../../utils/idempotencyKey';
import { useHomeStrings } from './useHomeStrings';
import type { CancellationReason } from '../../../types/client';

interface UseCancelRequestResult {
  submit: (bookingUuid: string, reason: CancellationReason) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  isDone: boolean;
  reset: () => void;
}

/**
 * Cancel lifecycle hook.
 * - Double-submit prevention (isSubmitting guard)
 * - Muvaffaqiyatda optimistic store update
 */
export function useCancelRequest(): UseCancelRequestResult {
  const STRINGS = useHomeStrings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  // idempotencyKey — hook tuzilishida bir marta, reset da yangilanadi
  const keyRef = useRef<string>(idempotencyKey());

  const setUpcomingBooking = useHomeStore((s) => s.setUpcomingBooking);

  const submit = async (
    bookingUuid: string,
    reason: CancellationReason,
  ): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await cancelRequestApi.requestCancel(bookingUuid, { reason });
      setUpcomingBooking(null);
      setIsDone(true);
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; code?: string }>;
      const status = axiosErr.response?.status;
      const code = axiosErr.response?.data?.code;

      let message: string = STRINGS.CANCEL_ERROR_GENERIC;

      if (status === 409 && code === 'BOOKING_CANCELLATION_DEADLINE_PASSED') {
        message = STRINGS.CANCEL_ERROR_FORBIDDEN;
      } else if (status === 409 || code === 'CANCELLATION_REQUEST_ALREADY_PENDING') {
        message = STRINGS.CANCEL_ERROR_ALREADY_PENDING;
      } else if (status === 404) {
        message = STRINGS.CANCEL_ERROR_NOT_FOUND;
      } else if (axiosErr.response?.data?.message) {
        message = axiosErr.response.data.message;
      }

      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setIsSubmitting(false);
    setError(null);
    setIsDone(false);
    keyRef.current = idempotencyKey();
  };

  return { submit, isSubmitting, error, isDone, reset };
}
